import { useState } from 'react';
import { FolderOpen, Euro, Mail, AlertTriangle, HelpCircle, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { getQuickActionVisibility } from '@/lib/quick-action-visibility';
import {
  buildAnschreibenDraft,
  buildMahnungDraft,
  buildSachstandDraft,
  MAHNUNG_STUFEN,
  type MahnStufe,
} from '@/lib/quick-action-drafts';
import { resolveMarkenNameForBusinessUnit } from '@/lib/branded-email';
import { supabase } from '@/lib/supabase';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
export { getQuickActionVisibility } from '@/lib/quick-action-visibility';

const DISABLED_BUTTON_TITLE = 'Folgt in Sub-Issue';

interface QuickActionButtonsProps {
  akteId: string;
  aktenNr?: string | null;
  versicherung_email?: string | null;
  business_unit?: string | null;
  forderungsakte_id?: string | null;
  zahlungsstatus?: string | null;
  onNavigateToAkte: (e: React.MouseEvent) => void;
  onOpenForderungsDrawer: (e: React.MouseEvent, akteId: string) => void;
  onOpenFristModal?: (e: React.MouseEvent, akteId: string) => void;
}

export default function QuickActionButtons({
  akteId,
  aktenNr,
  versicherung_email,
  business_unit,
  forderungsakte_id,
  zahlungsstatus,
  onNavigateToAkte,
  onOpenForderungsDrawer,
  onOpenFristModal,
}: QuickActionButtonsProps) {
  // Smoke-test hook: allows Playwright to verify the Error Boundary by triggering a controlled throw
  if (typeof window !== 'undefined' && (window as any).__SMOKE_ERROR_INJECTED) {
    throw new Error('[Smoke Test] Simulated QuickActionButtons render error for Error Boundary testing');
  }

  const { forderung: showForderung, forderungLabel, extras: showExtras } =
    getQuickActionVisibility(zahlungsstatus, forderungsakte_id);

  const [mahnungOpen, setMahnungOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  const btnBase =
    'inline-flex items-center gap-0.5 text-[9px] font-medium px-1.5 py-0.5 rounded transition-colors';
  const btnActive =
    `${btnBase} bg-gray-100 text-gray-600 hover:bg-gray-200 cursor-pointer`;
  const btnDisabled =
    `${btnBase} bg-gray-50 text-gray-300 cursor-not-allowed`;
  const btnBusy =
    `${btnBase} bg-gray-100 text-gray-400 cursor-wait`;

  const effectiveAktenNr = aktenNr || akteId;

  async function createDraft(type: 'anschreiben' | 'sachstand' | 'mahnung', stufe?: MahnStufe) {
    if (creating) return;
    setCreating(true);
    setMahnungOpen(false);
    try {
      const input = {
        aktenNr: effectiveAktenNr,
        versicherungEmail: versicherung_email ?? null,
      };

      let draftData;
      if (type === 'anschreiben') {
        draftData = buildAnschreibenDraft(input);
      } else if (type === 'mahnung' && stufe) {
        draftData = buildMahnungDraft(stufe, input);
      } else {
        draftData = buildSachstandDraft(input);
      }

      let bodyHtml = draftData.body_html;
      let bodyText = draftData.body_text;

      // Apply branded footer if a marke is configured for this business unit
      const markenName = resolveMarkenNameForBusinessUnit(business_unit);
      if (markenName) {
        const { data: marken } = await (supabase as any)
          .from('marken')
          .select('*')
          .eq('marken_name', markenName)
          .limit(1);
        const marke = marken?.[0];
        if (marke) {
          const { applyBrandingToHtml, applyBrandingToText } = await import('@/lib/branded-email');
          bodyHtml = applyBrandingToHtml(bodyHtml, marke);
          bodyText = applyBrandingToText(bodyText, marke);
        }
      }

      const { error } = await supabase.from('email_drafts').insert({
        recipient: draftData.recipient,
        subject: draftData.subject,
        body_text: bodyText,
        body_html: bodyHtml,
        akte_id: akteId,
        created_by_agent_id: 'quick-action-ui',
        status: 'draft',
      });

      if (error) {
        toast.error('Draft konnte nicht erstellt werden', { description: error.message });
        return;
      }

      if (draftData.requiresRecipient) {
        toast.warning('Draft erstellt — Empfänger fehlt', {
          description: 'E-Mail der Versicherung ist nicht hinterlegt. Bitte im Draft ergänzen.',
        });
      } else {
        toast.success('Draft erstellt', {
          description: `An ${draftData.recipient}`,
          action: { label: 'Zur Freigabe', onClick: () => window.open('/kommunikation/entwuerfe', '_blank') },
        });
      }
    } finally {
      setCreating(false);
    }
  }

  return (
    <div
      className="flex flex-wrap gap-1"
      onClick={(e) => e.stopPropagation()}
    >
      {/* 📂 Akte — always visible */}
      <span
        role="button"
        className={btnActive}
        data-testid="qab-akte"
        onClick={onNavigateToAkte}
      >
        <FolderOpen className="w-3 h-3" />
        Akte
      </span>

      {/* 💰 Forderung — hidden when bezahlt */}
      {showForderung && (
        <span
          role="button"
          className={btnActive}
          data-testid="qab-forderung"
          onClick={(e) => onOpenForderungsDrawer(e, akteId)}
        >
          <Euro className="w-3 h-3" />
          {forderungLabel}
        </span>
      )}

      {/* Following buttons — hidden when bezahlt or no forderungsakte */}
      {showExtras && (
        <>
          <span
            role="button"
            className={creating ? btnBusy : btnActive}
            data-testid="qab-anschreiben"
            onClick={() => createDraft('anschreiben')}
          >
            <Mail className="w-3 h-3" />
            Anschreiben
          </span>

          <Popover open={mahnungOpen} onOpenChange={setMahnungOpen}>
            <PopoverTrigger asChild>
              <span
                role="button"
                className={creating ? btnBusy : btnActive}
                data-testid="qab-mahnung"
              >
                <AlertTriangle className="w-3 h-3" />
                Mahnung
              </span>
            </PopoverTrigger>
            <PopoverContent className="w-40 p-1" align="start">
              <div className="flex flex-col gap-0.5">
                {MAHNUNG_STUFEN.map((s) => (
                  <button
                    key={s.value}
                    className="text-left text-xs px-2 py-1.5 rounded hover:bg-gray-100 transition-colors"
                    onClick={() => createDraft('mahnung', s.value)}
                    data-testid={`qab-mahnung-${s.value}`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>

          <span
            role="button"
            className={creating ? btnBusy : btnActive}
            data-testid="qab-sachstand"
            onClick={() => createDraft('sachstand')}
          >
            <HelpCircle className="w-3 h-3" />
            Sachstand
          </span>

          <span
            role="button"
            className={onOpenFristModal ? btnActive : btnDisabled}
            data-testid="qab-frist"
            title={onOpenFristModal ? undefined : DISABLED_BUTTON_TITLE}
            onClick={onOpenFristModal ? (e) => { e.stopPropagation(); onOpenFristModal(e, akteId); } : undefined}
          >
            <Calendar className="w-3 h-3" />
            Frist setzen
          </span>
        </>
      )}
    </div>
  );
}
